import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

import { useAuth } from '../../../context/AuthContext';
import { bookingsAPI, locationsAPI, pricingAPI, getServerTime } from '../../../api/bookings';
import { bruneiToday, bruneiDateOffset } from '../../../lib/brunei';
import { GuestIntent } from '../../../utils/storage';

import {
  buildTimeSlots,
  getConsecutiveSlots as computeConsecutiveSlots,
  isTimeSlotPast as computeIsTimeSlotPast,
  getOperatingHoursForDate,
  formatOperatingHoursDisplay,
} from './utils';
import { getSlotSpanAvailability } from '../../../lib/availability';

// All BookingFlow state, effects and handlers. Returns a single flat object
// so the shell can destructure without thinking.
export function useBookingFlow() {
  const navigate = useNavigate();
  const { user, isGuest, loading: authLoading } = useAuth();

  // ---- step + loading flags ----
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);

  // ---- location / date / time / duration ----
  const [locations, setLocations] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedTimeSlot, setSelectedTimeSlot] = useState('');
  const [duration, setDuration] = useState(1);
  const [availabilityMap, setAvailabilityMap] = useState({});
  const [availabilityLoading, setAvailabilityLoading] = useState(false);
  const [slotPricing, setSlotPricing] = useState({});

  // ---- courts requested + pricing ----
  const [courtsRequested, setCourtsRequested] = useState([
    { sport: 'basketball', quantity: 1, _uid: `court-${Date.now()}` },
  ]);
  const [courtsPricing, setCourtsPricing] = useState([]);

  // ---- misc form fields ----
  const [specialRequests, setSpecialRequests] = useState('');

  // ---- server time bounds (Brunei) ----
  const [serverDate, setServerDate] = useState('');
  const [serverTime, setServerTime] = useState('');
  const [minDate, setMinDate] = useState('');
  const [maxDate, setMaxDate] = useState('');

  // ---- initial-selection restoration bookkeeping ----
  const [initialSelection, setInitialSelection] = useState(null);
  const [selectionRestored, setSelectionRestored] = useState(false);

  // ---- in-progress flow snapshot (P2) ----
  // Persists the customer's WIP booking selection (location, date, time,
  // duration, courts, special requests) to sessionStorage so a 401-induced
  // re-login or accidental tab refresh doesn't wipe out their selection.
  // Cleared on successful submission and on logout.
  const FLOW_SNAPSHOT_KEY = 'bba_flow_snapshot';
  const FLOW_SNAPSHOT_MAX_AGE_MIN = 60;

  // ==================== DERIVED VALUES ====================
  const timeSlots = useMemo(
    () => buildTimeSlots(selectedLocation, selectedDate),
    [selectedLocation, selectedDate],
  );

  const isLocationClosed = useMemo(() => {
    const hours = getOperatingHoursForDate(selectedLocation, selectedDate);
    return hours?.closed === true;
  }, [selectedLocation, selectedDate]);

  const breakInfo = useMemo(() => {
    const hours = getOperatingHoursForDate(selectedLocation, selectedDate);
    if (!hours || !hours.breaks || hours.breaks.length === 0) return null;
    return hours.breaks;
  }, [selectedLocation, selectedDate]);

  const operatingHoursDisplay = useMemo(() => {
    const hours = getOperatingHoursForDate(selectedLocation, selectedDate);
    return hours ? formatOperatingHoursDisplay(hours) : null;
  }, [selectedLocation, selectedDate]);

  const availableSports = useMemo(() => {
    if (!selectedLocation?.sports) return ['Basketball'];
    return selectedLocation.sports;
  }, [selectedLocation]);

  // Per-slot availability status for the CURRENT duration (worst-case across
  // the consecutive span the booking would occupy). Pure client-side
  // recompute — changing duration never triggers a network call, only this
  // memo re-runs, so the "Fully Booked" state updates instantly.
  const slotAvailabilityStatuses = useMemo(() => {
    const out = {};
    timeSlots.forEach((slot) => {
      out[slot] = getSlotSpanAvailability(availabilityMap, timeSlots, slot, duration);
    });
    return out;
  }, [timeSlots, duration, availabilityMap]);

  const availability = useMemo(
    () => (selectedTimeSlot ? (slotAvailabilityStatuses[selectedTimeSlot] || null) : null),
    [selectedTimeSlot, slotAvailabilityStatuses],
  );

  // ==================== API LOADERS ====================
  const loadLocations = useCallback(async () => {
    try {
      const locs = await locationsAPI.getLocations();
      setLocations(locs);
    } catch (_error) {
      toast.error('Failed to load locations');
    }
  }, []);

  const loadServerTime = useCallback(async (hasRestoredSelection) => {
    try {
      const timeData = await getServerTime();
      setServerDate(timeData.date);
      setServerTime(timeData.time.substring(0, 5));
      setMinDate(timeData.date);

      if (!hasRestoredSelection && !selectedDate) {
        setSelectedDate(timeData.date);
      }

      const maxDateObj = new Date(timeData.date);
      maxDateObj.setMonth(maxDateObj.getMonth() + 1);
      setMaxDate(maxDateObj.toISOString().split('T')[0]);
    } catch (_error) {
      // Server clock unavailable — fall back to Brunei tz on the client,
      // never browser-local. Customers in other timezones still see the
      // correct calendar bounds.
      const today = bruneiToday();
      if (!hasRestoredSelection && !selectedDate) {
        setSelectedDate(today);
      }
      setMinDate(today);
      setMaxDate(bruneiDateOffset(30));
    }
    // selectedDate is intentionally omitted: we only set defaults when it's
    // empty, and re-running this when the date changes would clobber input.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // One-request bulk-fetch of availability for the whole day (mirrors the
  // public "Check Availability" page's loadAvailabilityAndPricing) instead
  // of only the one slot the customer has clicked — this is what lets
  // Step 1 show "Fully Booked" on every slot box up front, not just after
  // selection. Uses the batch endpoint (single DB query) rather than one
  // request per visible slot.
  const loadSlotAvailability = useCallback(async () => {
    if (!selectedLocation || !selectedDate || timeSlots.length === 0) {
      setAvailabilityMap({});
      return;
    }
    setAvailabilityLoading(true);
    try {
      const results = await bookingsAPI.checkAvailabilityBatch(selectedLocation.id, selectedDate);
      setAvailabilityMap(results || {});
    } catch (_error) {
      setAvailabilityMap({});
    } finally {
      setAvailabilityLoading(false);
    }
  }, [selectedLocation, selectedDate, timeSlots]);

  const loadSlotPricing = useCallback(async () => {
    if (!selectedLocation || !selectedDate || timeSlots.length === 0) return;
    try {
      const pricingData = await pricingAPI.calculateBatch(
        selectedLocation.id, selectedDate, 'basketball', 1,
      );
      setSlotPricing(pricingData || {});
    } catch (error) {
      console.error('Error loading slot pricing:', error);
      setSlotPricing({});
    }
  }, [selectedLocation, selectedDate, timeSlots]);

  const loadPricingInfo = useCallback(async () => {
    if (!selectedLocation || !selectedDate || !selectedTimeSlot) return;
    try {
      const pricingData = await Promise.all(
        courtsRequested.map(async (court) => {
          const response = await fetch(
            `${process.env.REACT_APP_BACKEND_URL}/api/pricing/calculate?` +
            `location_id=${selectedLocation.id}&date=${selectedDate}` +
            `&time_slot=${selectedTimeSlot}&sport=${court.sport}&duration=${duration}`,
          );
          return response.json();
        }),
      );
      setCourtsPricing(pricingData);
    } catch (error) {
      console.error('Error loading pricing:', error);
    }
  }, [selectedLocation, selectedDate, selectedTimeSlot, courtsRequested, duration]);

  // ==================== EFFECTS ====================
  // Initial mount: guard auth, restore any in-tab WIP snapshot or guest
  // pre-login intent, then load.
  //
  // NOTE: the OLD cross-page localStorage pre-fill (`bookingIntent`,
  // `bba_selection`, `hoopbook_selection`) was removed 2026-05-17 — those
  // keys persisted across BROWSER SESSIONS and showed stale data after
  // Confirmed/Cancelled/Expired bookings.
  // The per-tab `sessionStorage` WIP snapshot (below) and the newer
  // `GuestIntent` (utils/storage.js) both avoid that bug the same way:
  // sessionStorage dies with the tab and carries a short TTL, so neither
  // can resurface stale data days later.
  // Guards the intent/snapshot restoration below from running twice on the
  // same real mount. React StrictMode (dev only) double-invokes mount
  // effects; without this guard, the 1st run consumes + clears the guest
  // intent, and the 2nd run (finding it already gone) falls through to the
  // loadServerTime fallback, which resets selectedDate back to today.
  const restoreAttemptedRef = useRef(false);

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      navigate('/auth');
      return;
    }
    if (restoreAttemptedRef.current) return;
    restoreAttemptedRef.current = true;
    let hasRestoredSelection = false;

    // Two candidate sources can pre-fill this page:
    // 1) an in-tab WIP snapshot (401 mid-flow recovery / refresh resume)
    // 2) a guest's pre-login landing-page intent (see AuthPage/AuthCallback)
    // Whichever was saved MORE RECENTLY wins — a stale leftover snapshot
    // from an earlier, abandoned /book visit must never override a guest's
    // selection made moments ago (and vice versa).
    let snapshot = null;
    try {
      const snapRaw = sessionStorage.getItem(FLOW_SNAPSHOT_KEY);
      if (snapRaw) {
        const snap = JSON.parse(snapRaw);
        const ageMin = (Date.now() - (snap.savedAt || 0)) / 60000;
        if (ageMin < FLOW_SNAPSHOT_MAX_AGE_MIN) {
          snapshot = snap;
        } else {
          sessionStorage.removeItem(FLOW_SNAPSHOT_KEY);
        }
      }
    } catch (e) {
      console.error('Error parsing flow snapshot:', e);
      sessionStorage.removeItem(FLOW_SNAPSHOT_KEY);
    }

    const intent = GuestIntent.get();

    const useSnapshot = snapshot && (!intent || (snapshot.savedAt || 0) >= (intent.savedAt || 0));
    const useIntent = intent && !useSnapshot;

    if (useSnapshot) {
      const snap = snapshot;
      if (snap.date) setSelectedDate(snap.date);
      if (snap.timeSlot) setSelectedTimeSlot(snap.timeSlot);
      if (snap.duration) setDuration(snap.duration);
      if (Array.isArray(snap.courtsRequested) && snap.courtsRequested.length) {
        setCourtsRequested(snap.courtsRequested);
      }
      if (typeof snap.specialRequests === 'string') {
        setSpecialRequests(snap.specialRequests);
      }
      if (typeof snap.step === 'number' && snap.step >= 1 && snap.step <= 3) {
        setStep(snap.step);
      }
      setInitialSelection({ type: 'session', data: snap });
      hasRestoredSelection = true;
      // The guest-intent (if any) is now stale relative to this snapshot —
      // discard it so it can't resurface on a later visit.
      GuestIntent.clear();
    } else if (useIntent) {
      if (intent.date) setSelectedDate(intent.date);
      if (intent.timeSlot) setSelectedTimeSlot(intent.timeSlot);
      setInitialSelection({ type: 'guest_intent', data: intent });
      hasRestoredSelection = true;
      sessionStorage.removeItem(FLOW_SNAPSHOT_KEY);
      GuestIntent.clear();
      toast.info("We've pre-filled your previous selection — please confirm it's still available.", {
        duration: 6000,
      });
    }

    setSelectionRestored(hasRestoredSelection);
    loadServerTime(hasRestoredSelection);
    loadLocations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, authLoading]);

  // Apply preselected location once locations are loaded (in-tab WIP only).
  useEffect(() => {
    if (locations.length === 0) return;
    if (initialSelection) {
      const locationId = initialSelection.data.locationId;
      if (locationId) {
        const location = locations.find((l) => l.id === locationId);
        if (location) {
          setSelectedLocation(location);
          return;
        }
      }
    }
    if (!selectedLocation) setSelectedLocation(locations[0]);
    // selectedLocation intentionally omitted — we only want to auto-pick
    // "first location" once when no restoration happened.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locations, initialSelection]);

  useEffect(() => {
    if (selectedLocation && selectedDate && timeSlots.length > 0) {
      loadSlotPricing();
      loadSlotAvailability();
    } else {
      setAvailabilityMap({});
    }
  }, [selectedLocation, selectedDate, timeSlots, loadSlotPricing, loadSlotAvailability]);

  useEffect(() => {
    if (selectedDate && selectedTimeSlot && selectedLocation) {
      loadPricingInfo();
    }
  }, [selectedDate, selectedTimeSlot, selectedLocation, courtsRequested, duration,
      loadPricingInfo]);

  // P2: snapshot the in-progress flow to sessionStorage so a 401 / re-login
  // / accidental refresh doesn't wipe the customer's selection.
  // Only writes once the user has actually started selecting (location +
  // date) — avoids polluting sessionStorage on first mount.
  useEffect(() => {
    if (!user) return;
    if (!selectedLocation || !selectedDate) return;
    try {
      const snap = {
        locationId: selectedLocation?.id,
        locationName: selectedLocation?.name,
        date: selectedDate,
        timeSlot: selectedTimeSlot,
        duration,
        courtsRequested,
        specialRequests,
        step,
        savedAt: Date.now(),
      };
      sessionStorage.setItem(FLOW_SNAPSHOT_KEY, JSON.stringify(snap));
    } catch (e) {
      // Storage full / disallowed (incognito, quota exceeded). The snapshot
      // is a comfort feature — log so a real bug surfaces in DevTools but
      // don't break the booking flow.
      console.warn('Could not save booking-flow snapshot:', e?.message || e);
    }
  }, [user, selectedLocation, selectedDate, selectedTimeSlot, duration,
      courtsRequested, specialRequests, step]);

  // ==================== HANDLERS ====================
  const handleLocationChange = useCallback((locationId) => {
    const location = locations.find((l) => l.id === locationId);
    setSelectedLocation(location);
    setSelectedTimeSlot('');
    setSlotPricing({});
    setAvailabilityMap({});
  }, [locations]);

  const getConsecutiveSlots = useCallback(
    (startSlot, dur) => computeConsecutiveSlots(timeSlots, startSlot, dur),
    [timeSlots],
  );

  const isTimeSlotPast = useCallback(
    (slot) => computeIsTimeSlotPast(slot, serverDate, serverTime, selectedDate),
    [serverDate, serverTime, selectedDate],
  );

  const checkConsecutiveSlotsAvailability = useCallback(async (startSlot, dur) => {
    if (!selectedLocation || !selectedDate || dur === 1) return true;
    const consecutive = computeConsecutiveSlots(timeSlots, startSlot, dur);
    if (consecutive.length < dur) {
      toast.error(`Not enough consecutive time slots available after ${startSlot}`);
      return false;
    }
    try {
      for (const slot of consecutive) {
        const avail = await bookingsAPI.checkAvailability(
          selectedLocation.id, selectedDate, slot,
        );
        if (avail.available_courts === 0) {
          toast.error(`Time slot ${slot} is fully booked. Please select a different starting time.`);
          return false;
        }
      }
      return true;
    } catch (error) {
      console.error('Error checking consecutive availability:', error);
      return false;
    }
  }, [selectedLocation, selectedDate, timeSlots]);

  const handleTimeSlotSelection = useCallback(async (slot) => {
    if (duration > 1) {
      const ok = await checkConsecutiveSlotsAvailability(slot, duration);
      if (ok) {
        setSelectedTimeSlot(slot);
        const consecutive = computeConsecutiveSlots(timeSlots, slot, duration);
        toast.success(`Selected ${duration}-hour session: ${consecutive.join(', ')}`);
      }
    } else {
      setSelectedTimeSlot(slot);
    }
  }, [duration, timeSlots, checkConsecutiveSlotsAvailability]);

  const addCourt = useCallback(() => {
    setCourtsRequested((prev) => {
      if (prev.length >= 5) return prev;
      return [
        ...prev,
        {
          sport: 'basketball',
          quantity: 1,
          _uid: `court-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        },
      ];
    });
  }, []);

  const removeCourt = useCallback((index) => {
    setCourtsRequested((prev) => prev.length > 1 ? prev.filter((_, i) => i !== index) : prev);
  }, []);

  const updateCourtSport = useCallback((index, sport) => {
    setCourtsRequested((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], sport };
      return updated;
    });
  }, []);

  // "Next" on step 1 — fully booked slots are now a hard stop.
  const handleDateTimeNext = useCallback(async () => {
    if (!selectedDate || !selectedTimeSlot) {
      toast.error('Please select date and time');
      return;
    }

    if (availability?.is_fully_booked) {
      toast.error('This slot is fully booked. Please pick a different time.');
      return;
    }

    setStep(2);
  }, [selectedDate, selectedTimeSlot, availability]);

  const handleSubmitBooking = useCallback(async () => {
    if (!selectedLocation) { toast.error('Please select a location'); return; }
    if (!selectedDate || !selectedTimeSlot) {
      toast.error('Please select date and time');
      return;
    }
    if (availability?.is_fully_booked) {
      toast.error('This slot is fully booked. Please pick a different time.');
      return;
    }

    // Step A: Double-validate availability just before payment so a
    // concurrent customer can't snipe the slot during our checkout.
    setLoading(true);
    try {
      const freshAvail = await bookingsAPI.checkAvailability(
        selectedLocation.id, selectedDate, selectedTimeSlot,
      );
      const courtsNeeded = courtsRequested.length;
      if (freshAvail.available_courts < courtsNeeded) {
        toast.error(
          'Sorry, this slot was just booked by someone else. Please select another time.',
          { duration: 6000 },
        );
        setStep(1);
        setSelectedTimeSlot('');
        setLoading(false);
        return;
      }
    } catch (_e) {
      toast.error('Failed to verify availability. Please try again.');
      setLoading(false);
      return;
    }

    try {
      // Step B: Create booking — backend now creates it in AWAITING_PAYMENT
      // status with courts auto-assigned and a 10-min payment hold.
      const bookingData = {
        user_id: isGuest ? null : user?.id,
        guest_phone: isGuest ? user?.phone : null,
        location_id: selectedLocation.id,
        booking_date: selectedDate,
        time_slot: selectedTimeSlot,
        duration,
        courts_requested: courtsRequested,
        special_requests: specialRequests,
      };
      const booking = await bookingsAPI.createBooking(bookingData);

      localStorage.removeItem('bookingIntent');
      sessionStorage.removeItem(FLOW_SNAPSHOT_KEY);
      GuestIntent.clear();

      // Step C: Initiate Pocket Pay and redirect the customer.
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const resp = await fetch(`${API_URL}/api/bookings/${booking.id}/payment`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({}),
      });
      const data = await resp.json();
      if (!resp.ok) {
        const detail = data?.detail;
        const msg = typeof detail === 'string'
          ? detail
          : (detail?.message || 'Failed to start payment. Please try again.');
        throw new Error(msg);
      }
      if (data.payment_url) {
        toast.success('Redirecting to secure payment...', { duration: 1500 });
        setTimeout(() => { window.location.href = data.payment_url; }, 800);
      } else {
        // Fallback to the in-app payment page so the customer can retry.
        navigate(`/payment/${booking.id}`);
      }
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail || err?.message;
      if (status === 409) {
        toast.error(
          typeof detail === 'string'
            ? detail
            : 'Sorry, this slot was just booked by someone else. Please select another time.',
          { duration: 6000 },
        );
        setStep(1);
        setSelectedTimeSlot('');
      } else if (status === 400 && typeof detail === 'string' && detail.includes('court(s) available')) {
        toast.error(detail, { duration: 6000 });
      } else {
        toast.error(typeof detail === 'string' ? detail : 'Failed to submit booking');
      }
    } finally {
      setLoading(false);
    }
  }, [selectedLocation, selectedDate, selectedTimeSlot, availability, isGuest, user,
      duration, courtsRequested, specialRequests, navigate]);

  // Step navigation (back button + explicit step changes)
  const goBack = useCallback(() => {
    if (step === 1) navigate('/home');
    else setStep(step - 1);
  }, [step, navigate]);

  const goToStep = useCallback((s) => setStep(s), []);

  const handleDateChange = useCallback((value) => {
    setSelectedDate(value);
    setSelectedTimeSlot('');  // clear when date changes
  }, []);

  const handleDurationChange = useCallback((hours) => {
    setDuration(hours);
    setSelectedTimeSlot('');  // clear when duration changes
  }, []);

  return {
    // identity
    user, isGuest,

    // step + flags
    step, loading,

    // data
    locations, selectedLocation, selectedDate, selectedTimeSlot, duration,
    availability, slotAvailabilityStatuses, availabilityLoading,
    slotPricing, courtsRequested, courtsPricing,
    specialRequests,

    // derived
    timeSlots, isLocationClosed, breakInfo, operatingHoursDisplay, availableSports,

    // helpers
    isTimeSlotPast, getConsecutiveSlots,

    // time bounds
    minDate, maxDate,

    // actions
    goBack, goToStep,
    handleLocationChange,
    handleDateChange, handleDurationChange,
    handleTimeSlotSelection,
    handleDateTimeNext,
    addCourt, removeCourt, updateCourtSport,
    setSpecialRequests,
    handleSubmitBooking,

    // (exposed so the shell can call setSelectionRestored if ever needed; we
    // keep it here to avoid dead-code imports in the shell)
    _internal: { selectionRestored },
  };
}
